import { GoliathShapingManagementConfigModule } from '@digiwall/goliath-shaping-management/config';
import { GoliathAccountingConfigModule } from '@digiwall/goliath-accounting/config';
import { provideFeatureManagementConfig } from '@abp/ng.feature-management';
import { provideAccountConfig } from '@abp/ng.account/config';
import { GoliathContactsConfigModule } from '@digiwall/goliath-contacts/config';
import { GoliathCoreConfigModule } from '@digiwall/goliath-core/config';
import {
  GoliathDocumentsConfigModule,
  ParametersConfigModule,
  ProductionConfigModule,
} from '@digiwall/goliath-documents/config';
import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { CHECK_AUTHENTICATION_STATE_FN_KEY, CoreModule, provideAbpCore, withOptions } from '@abp/ng.core';
import { registerLocale } from '@abp/ng.core/locale';
import { VALIDATION_ERROR_TEMPLATE } from '@ngx-validate/core';

import { APP_INITIALIZER, Injector, NgModule, inject, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { provideAbpOAuth } from '@abp/ng.oauth';
import { checkAuthenticationStateOrLogin } from './check-authentication-state-or-login';

import { ConfigService, ThemeBasicModule } from '@abp/ng.theme.basic';
import {
  CUSTOM_ERROR_HANDLERS,
  provideAbpThemeShared,
  ThemeSharedModule,
  withConfirmationIcon,
} from '@abp/ng.theme.shared';
import { ServiceWorkerModule } from '@angular/service-worker';

import { AG_GRID_PAGINATION_SIZE_SELECTOR, DwAgGridModule } from '@digiwall/ag-grid';
import { DW_CORE_POLICIES_PROVIDER, LocalizationResourceName } from '@digiwall/core-features';

import { ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { RoutePaths } from './enums';
import { initialConfig, provideNgxMask } from 'ngx-mask';
import {
  COUNTRY_SEEDING_CONFIG_TOKEN,
  COUNTRY_TRANSLATIONS,
  DwDateAdapter,
  DwDateParserFormatter,
  ErrorComponent,
  ErrorFormControlDirective,
  ErrorNotifierComponent,
  HttpRequestInterceptor,
  ICountryStoreToken,
  ILocalityServiceToken,
  IUserStoreToken,
  PHONE_OPTION_PROVIDER,
  provideConfigStateService,
  provideCurrencyPipe,
  provideDatePipe,
  provideDecimalPipe,
  provideOdysseyOffcanvas,
  PrototypeExtensionsModule,
  TemplatePageTitleStrategy,
  CustomFormatErrorHandlerService,
  RegexPattern,
} from '@digiwall/core';
import { ReportingConfigModule } from '@odyssey/reporting/config';
import {
  CountryStore,
  UserStore,
  EnvironmentService,
  provideEnvironmentService,
} from '@digiwall/goliath-core';

import '@angular/common/locales/global/fr';
import '@angular/common/locales/global/en';
import '@angular/common/locales/global/nl';
import { CompanyService, LocalityService } from '@digiwall/goliath-core/proxy';
import {
  FloatingButtonComponent,
  FloatingContainerComponent,
  FloatingTrackComponent,
  SpinningLoaderComponent,
  provideNgSelectCtrlMultiSelect,
} from '@digiwall/ui-kit';
import { EUROPA_ISO_2_CODES } from './country/europa-iso-2';
import { TitleStrategy } from '@angular/router';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { GoliathFeaturesConfigModule } from './features/goliath-features-config.module';
import { GoliathProductsConfigModule } from '@digiwall/goliath-products/config';
import { provideRequiredLanguagesProvider } from 'projects/goliath-core/src/lib/shared/company-required-languages.service';

const countriesFactory = (injector: Injector) => {
  const countryStore = injector.get(CountryStore);
  return countryStore.countriesList;
};
function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
@NgModule({
  imports: [
    PrototypeExtensionsModule,
    GoliathShapingManagementConfigModule.forRoot(),
    GoliathAccountingConfigModule.forRoot(),
    ReportingConfigModule.forRoot(),
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    CoreModule.forChild({
      localizations: [
        {
          culture: 'en',
          resources: [
            {
              resourceName: 'ERP',
              texts: {
                HomePage: 'Home',
              },
            },
          ],
        },
        {
          culture: 'fr',
          resources: [
            {
              resourceName: 'ERP',
              texts: {},
            },
          ],
        },
      ],
    }),
    ThemeSharedModule,
    ErrorComponent,
    ThemeBasicModule.forRoot(),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    GoliathCoreConfigModule.forRoot(RoutePaths.General),
    GoliathContactsConfigModule.forRoot(RoutePaths.ThirdParty),
    GoliathProductsConfigModule.forRoot(RoutePaths.Product),
    GoliathDocumentsConfigModule.forRoot(RoutePaths.Documents),
    GoliathFeaturesConfigModule.forRoot(),
    DwAgGridModule,
    ReactiveFormsModule,
    ProductionConfigModule.forRoot(RoutePaths.Production),
    ParametersConfigModule.forRoot(RoutePaths.Parameters),
    NgSelectModule,
    //NgOptionHighlightModule,
    FloatingTrackComponent,
    FloatingContainerComponent,
    FloatingButtonComponent,
    ErrorNotifierComponent,
    ErrorFormControlDirective,
    SpinningLoaderComponent,
  ],
  declarations: [AppComponent],
  providers: [
    APP_ROUTE_PROVIDER,
    provideAbpCore(
      withOptions({
        environment,
        registerLocaleFn: registerLocale(),
        localizations: [
          {
            culture: 'en',
            resources: [
              {
                resourceName: 'ERP',
                texts: {},
              },
            ],
          },
          {
            culture: 'fr',
            resources: [
              {
                resourceName: 'ERP',
                texts: {},
              },
            ],
          },
        ],
      }),
    ),
    provideAbpThemeShared(
      withConfirmationIcon({
        info: 'digi-out-alert-info',
        success: 'digi-out-thumb',
        warning: 'digi-out-alert-warning',
        error: 'digi-out-cross-circle',
        default: 'digi-out-question-circle',
      }),
    ),
    provideAbpOAuth(),
    // Override ABP's default `checkAccessToken` self-heal so that a clock-valid
    // token with an anonymous backend user (e.g. after the on-prem container
    // restart rotates the OpenIddict keys) forces a clean re-login instead of
    // leaving the SPA in a degraded, half-logged-in state. Must come AFTER
    // provideAbpOAuth() so this value wins for the (non-multi) token.
    {
      provide: CHECK_AUTHENTICATION_STATE_FN_KEY,
      useValue: checkAuthenticationStateOrLogin,
    },
    provideAccountConfig(),
    provideFeatureManagementConfig(),
    {
      provide: TitleStrategy,
      useClass: TemplatePageTitleStrategy,
    },
    {
      provide: VALIDATION_ERROR_TEMPLATE,
      useValue: ErrorComponent,
    },
    { provide: NgbDateAdapter, useClass: DwDateAdapter },
    { provide: ICountryStoreToken, useExisting: CountryStore },
    { provide: ILocalityServiceToken, useExisting: LocalityService },
    { provide: IUserStoreToken, useExisting: UserStore },
    { provide: NgbDateParserFormatter, useClass: DwDateParserFormatter },
    { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
    {
      provide: CUSTOM_ERROR_HANDLERS,
      useExisting: CustomFormatErrorHandlerService,
      multi: true,
    },
    provideNgxMask(() => ({
      ...initialConfig,
      patterns: {
        ...initialConfig.patterns,
        B: { pattern: new RegExp(RegexPattern.AlphanumericRegex), optional: true },
        T: { pattern: new RegExp(RegexPattern.LetterRegex), optional: true },
      },
    })),
    provideOdysseyOffcanvas(),
    provideNgSelectCtrlMultiSelect(),
    provideDatePipe(),
    provideCurrencyPipe(),
    provideDecimalPipe(),
    ConfigService,
    provideConfigStateService(),
    provideEnvironmentService(),
    provideRequiredLanguagesProvider(),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (configService: ConfigService, companyService: CompanyService) => {
        let environmentService = inject(EnvironmentService);
        return async () => {
          await environmentService.waitUntilLoadedAsync();
          await configService.loadConfig(companyService.getDisplayConfiguration);
        };
      },
      deps: [ConfigService, CompanyService],
    },
    {
      provide: COUNTRY_TRANSLATIONS,
      useFactory: countriesFactory,
      deps: [Injector],
    },
    {
      provide: LocalizationResourceName,
      useValue: 'ERP',
    },
    {
      provide: COUNTRY_SEEDING_CONFIG_TOKEN,
      useValue: {
        countryAlreadySeeded: true,
        localityCountryCodesSeeded: ['BE', 'LU', 'FR'],
      },
    },
    {
      provide: PHONE_OPTION_PROVIDER,
      useValue: {
        preferredCountries: ['be'],
        onlyCountries: EUROPA_ISO_2_CODES,
      },
    },
    // Provide empty permissions for now
    {
      provide: DW_CORE_POLICIES_PROVIDER,
      useValue: {},
    },
    {
      provide: AG_GRID_PAGINATION_SIZE_SELECTOR,
      useValue: [500, 1000, 2500],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
